import { useState } from "react";
import type { ChangeEvent } from "react";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CloseIcon from "@mui/icons-material/Close";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import {
  Box,
  Checkbox,
  FormControlLabel,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";

import type { CampaignChannelOption } from "@/features/campaigns/shared/helpers/channelSelectionHelper";

type ChannelFilterSwitchesProps = {
  channels: CampaignChannelOption[];
  selectedChannelIds?: Array<string | number>;
  blockedChannelIds?: Array<string | number>;
  title?: string;
  helperText?: string;
  lockLastSelectedChannel?: boolean;
  onChange?: (_selectedChannelIds: string[]) => void;
};

export default function ChannelFilterSwitches({
  channels,
  selectedChannelIds = [],
  blockedChannelIds = [],
  title = "Select the channels you want in your campaign",
  helperText = "Channels not selected won’t run as part of the package. Only selected channels will run, and at least one channel must remain selected.",
  lockLastSelectedChannel = false,
  onChange,
}: ChannelFilterSwitchesProps) {
  const [uncheckedChannelWarning, setUncheckedChannelWarning] = useState<{
    id: string;
    message: string;
  } | null>(null);
  const selectedChannelIdSet = new Set(
    selectedChannelIds.map((channel) => String(channel))
  );
  const blockedChannelIdSet = new Set(
    blockedChannelIds.map((channel) => String(channel))
  );
  const hasLockedSelectedChannel =
    lockLastSelectedChannel && selectedChannelIdSet.size <= 1;

  const handleChannelToggle =
    (channel: CampaignChannelOption) =>
    (event: ChangeEvent<HTMLInputElement>) => {
      const channelId = String(channel.id);

      if (event.target.checked) {
        if (uncheckedChannelWarning) {
          setUncheckedChannelWarning(null);
        }
        onChange?.([...selectedChannelIdSet, channelId]);
        return;
      }

      if (
        (lockLastSelectedChannel && selectedChannelIdSet.size <= 1) ||
        blockedChannelIdSet.has(channelId)
      ) {
        if (lockLastSelectedChannel && selectedChannelIdSet.size <= 1) {
          setUncheckedChannelWarning({
            id: channelId,
            message: `${channel.name} is the only channel selected. Add another before removing it.`,
          });
        }
        return;
      }

      setUncheckedChannelWarning({
        id: channelId,
        message: `${channel.name} is unchecked and won’t run as part of your package.`,
      });
      onChange?.([...selectedChannelIdSet].filter((id) => id !== channelId));
    };

  if (!channels.length) {
    return null;
  }

  return (
    <Box className="creative-channel-toggles">
      {uncheckedChannelWarning ? (
        <Box
          className="creative-channel-warning-banner d-flex align-items-center"
          role="alert"
        >
          <InfoOutlinedIcon className="creative-channel-warning-icon" />
          <Typography
            variant="caption"
            className="creative-channel-warning-text"
          >
            {uncheckedChannelWarning.message}
          </Typography>
          <IconButton
            className="creative-channel-warning-close"
            size="small"
            aria-label="Close channel warning"
            onClick={() => setUncheckedChannelWarning(null)}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      ) : null}
      <Typography
        variant="caption"
        className="creative-channel-toggles-title fw-b d-block"
      >
        {title}
      </Typography>
      <Box className="creative-channel-toggles-note d-flex align-items-center">
        <InfoOutlinedIcon sx={{ fontSize: 12, mr: 0.5 }} />
        <Typography variant="caption">{helperText}</Typography>
      </Box>
      <Box className="creative-channel-toggle-list d-flex flex-wrap align-items-center">
        {channels.map((channel) => {
          const channelId = String(channel.id);
          const checked = selectedChannelIdSet.has(channelId);
          const isLockedByLastChannelRule = checked && hasLockedSelectedChannel;
          const isLockedByPackageRule =
            checked && blockedChannelIdSet.has(channelId);
          const isLockedOn = isLockedByLastChannelRule || isLockedByPackageRule;

          return (
            <Tooltip
              key={channel.id}
              arrow
              disableHoverListener={!isLockedByLastChannelRule}
              title={
                isLockedByLastChannelRule
                  ? "At least one channel must remain selected."
                  : ""
              }
            >
              <Box
                className={`creative-channel-toggle d-inline-flex align-items-center ${
                  isLockedOn ? "creative-channel-toggle-locked" : ""
                }`}
              >
                <FormControlLabel
                  className="creative-channel-switch-label m-0"
                  control={
                    <Checkbox
                      className={`channel-filter-checkbox ${
                        isLockedOn ? "channel-filter-checkbox-locked" : ""
                      }`}
                      checked={checked}
                      onChange={handleChannelToggle(channel)}
                    />
                  }
                  label={
                    <Box
                      component="span"
                      className="creative-channel-switch-label-content d-inline-flex align-items-center"
                    >
                      <span>{channel.name}</span>
                      {isLockedOn ? (
                        <LockOutlinedIcon
                          className="creative-channel-lock-icon"
                          aria-hidden="true"
                          data-testid={`${channel.name}-locked-icon`}
                        />
                      ) : null}
                    </Box>
                  }
                />
              </Box>
            </Tooltip>
          );
        })}
      </Box>
    </Box>
  );
}
